import React, { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import LightConsumption from "./LightConsumption";
import LightUsers from "./LightUsers";
import LightExports from "./LightExports";
import { getAvailableReports } from "services/edge/ReportService";

const useStyles = makeStyles()(() => ({
  layout: {
    display: "flex",
    gap: "1.5rem",
    alignItems: "flex-start",
    [MEDIA.smDown]: { flexDirection: "column", gap: "1rem" },
  },
  sideNav: {
    width: "220px",
    flexShrink: 0,
    backgroundColor: BRAND.white,
    borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`,
    boxShadow: SHADOW.soft,
    overflow: "hidden",
    position: "sticky",
    top: "80px",
    [MEDIA.smDown]: {
      width: "100%", position: "static", top: "auto",
      display: "flex", flexDirection: "row",
      overflowX: "auto", WebkitOverflowScrolling: "touch",
      borderRadius: RADIUS.md,
    },
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.65rem",
    padding: "0.85rem 1.1rem",
    cursor: "pointer",
    fontFamily: FONT.family,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: BRAND.textMuted,
    transition: "all 0.15s",
    borderLeft: "3px solid transparent",
    "&:hover": {
      backgroundColor: BRAND.background,
      color: BRAND.textMain,
    },
    [MEDIA.smDown]: {
      padding: "0.65rem 0.9rem",
      borderLeft: "none",
      borderBottom: "3px solid transparent",
      whiteSpace: "nowrap", flexShrink: 0,
    },
  },
  navItemActive: {
    backgroundColor: BRAND.tertiary,
    color: BRAND.primary,
    fontWeight: 700,
    borderLeftColor: BRAND.primary,
    "&:hover": {
      backgroundColor: BRAND.tertiary,
      color: BRAND.primary,
    },
    [MEDIA.smDown]: {
      borderLeftColor: "transparent",
      borderBottomColor: BRAND.primary,
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    [MEDIA.smDown]: { width: "100%" },
  },
  placeholder: {
    backgroundColor: BRAND.white,
    borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`,
    boxShadow: SHADOW.soft,
    padding: "2rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1.25rem",
  },
}));

export default function LightAccount() {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [section, setSection] = useState("offer");
  const canViewUsers   = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_USER);
  const hasReportsPrivilege = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS, PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL);

  // Précharge les rapports disponibles dès l'ouverture (même règle que mode avancé : display si length > 0)
  useEffect(() => {
    if (state.account?.id && hasReportsPrivilege) {
      getAvailableReports(state.account.id, dispatch);
    }
  }, [state.account?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const canViewReports = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS, PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL)
    && (state.availableReports?.length > 0);

  return (
    <Box className={light.pageRoot}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner}>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.account.title")}</Typography>
          <Typography className={light.pageSubtitleWhite}>{i18next.t("light.account.subtitle")}</Typography>
        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>
        <Box className={s.layout}>
          {/* Side nav */}
          <Box className={s.sideNav}>
            <Box
              className={cx(s.navItem, section === "offer" && s.navItemActive)}
              onClick={() => setSection("offer")}
            >
              <StorefrontOutlinedIcon style={{ fontSize: "18px" }} />
              {i18next.t("light.account.menuOffer")}
            </Box>
            {canViewUsers && (
              <Box
                className={cx(s.navItem, section === "users" && s.navItemActive)}
                onClick={() => setSection("users")}
              >
                <PeopleOutlinedIcon style={{ fontSize: "18px" }} />
                {i18next.t("light.account.menuUsers")}
              </Box>
            )}
            {canViewReports && (
              <Box
                className={cx(s.navItem, section === "exports" && s.navItemActive)}
                onClick={() => setSection("exports")}
              >
                <FileDownloadOutlinedIcon style={{ fontSize: "18px" }} />
                {i18next.t("light.exports.title")}
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box className={s.content}>
            {section === "offer" && (
              <>
                <Box className={s.placeholder}>
                  <InfoOutlinedIcon style={{ fontSize: "18px", color: BRAND.textMuted }} />
                  <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family }}>
                    {i18next.t("light.account.planPlaceholder")}
                  </Typography>
                </Box>
                <LightConsumption embedded />
              </>
            )}
            {section === "users" && canViewUsers && (
              <LightUsers />
            )}
            {section === "exports" && canViewReports && (
              <LightExports />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
